/**
 * useYearEndPrompt.js
 *
 * 연말정산 팝업 표시 로직을 관리하는 훅
 * - LocalStorage로 표시 빈도 관리
 * - 24시간 제한, 3회 무시 시 영구 무시
 */

import { useState, useCallback, useMemo } from 'react';

const STORAGE_KEY_PREFIX = 'yearEndPrompt_';
const MAX_DISMISS_COUNT = 3;
const HOURS_BETWEEN_PROMPTS = 24;

/**
 * LocalStorage에서 프롬프트 상태 가져오기
 */
const getStoredState = (year) => {
  try {
    const key = `${STORAGE_KEY_PREFIX}${year}`;
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

/**
 * LocalStorage에 프롬프트 상태 저장
 */
const setStoredState = (year, state) => {
  try {
    const key = `${STORAGE_KEY_PREFIX}${year}`;
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // LocalStorage 사용 불가 시 무시
  }
};

/**
 * 프롬프트 표시 여부 계산
 */
const calculateShouldShow = (year) => {
  const stored = getStoredState(year);

  // 저장된 상태가 없으면 표시
  if (!stored) return true;

  // 영구 무시된 경우
  if (stored.permanentlyDismissed) return false;

  // 최대 무시 횟수 초과
  if (stored.dismissCount >= MAX_DISMISS_COUNT) return false;

  // 마지막 표시 후 24시간 이내
  if (stored.lastShown) {
    const lastShown = new Date(stored.lastShown);
    const hoursSince = (Date.now() - lastShown.getTime()) / (1000 * 60 * 60);
    if (hoursSince < HOURS_BETWEEN_PROMPTS) return false;
  }

  return true;
};

/**
 * useYearEndPrompt
 *
 * @param {number} year - 연도 (기본값: 2025)
 * @returns {Object}
 *   - shouldShow: boolean - 팝업 표시 여부
 *   - isOpen: boolean - 모달 열림 상태
 *   - openPrompt: function - 모달 열기
 *   - dismissPrompt: function - 모달 닫기 (카운트 증가)
 *   - permanentlyDismiss: function - 영구 닫기
 *   - markAsShown: function - 표시됨으로 마킹
 */
export function useYearEndPrompt(year = 2025) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  // 표시 여부 계산
  const shouldShow = useMemo(() => {
    if (hasBeenShown) return false;
    return calculateShouldShow(year);
  }, [year, hasBeenShown]);

  // 모달 열기
  const openPrompt = useCallback(() => {
    setIsOpen(true);
  }, []);

  // 모달 닫기 (나중에 버튼) - 카운트 증가
  const dismissPrompt = useCallback(() => {
    setIsOpen(false);
    setHasBeenShown(true);

    const stored = getStoredState(year) || {
      dismissCount: 0,
      permanentlyDismissed: false
    };

    setStoredState(year, {
      ...stored,
      lastShown: new Date().toISOString(),
      dismissCount: stored.dismissCount + 1
    });
  }, [year]);

  // 영구 닫기 (다시 보지 않기)
  const permanentlyDismiss = useCallback(() => {
    setIsOpen(false);
    setHasBeenShown(true);

    const stored = getStoredState(year) || {
      dismissCount: 0
    };

    setStoredState(year, {
      ...stored,
      lastShown: new Date().toISOString(),
      permanentlyDismissed: true
    });
  }, [year]);

  // 표시됨으로 마킹 (연말정산 완료 시)
  const markAsShown = useCallback(() => {
    setHasBeenShown(true);
  }, []);

  return {
    shouldShow,
    isOpen,
    openPrompt,
    dismissPrompt,
    permanentlyDismiss,
    markAsShown
  };
}

export default useYearEndPrompt;
